"use client";

import { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { motion, useInView } from "framer-motion";
import { FaUsers, FaHandshake, FaEye, FaArrowRight } from "react-icons/fa";

const AccountabilitySection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { threshold: 0.2, once: true });

  useEffect(() => {
    if (isInView) {
      setIsVisible(true);
    }
  }, [isInView]);

  const accountabilityPrinciples = [
    {
      icon: FaUsers,
      title: "Community Involvement",
      description:
        "Engaging communities as key partners and stakeholders, not simply beneficiaries",
    },
    {
      icon: FaHandshake,
      title: "Active Commitment",
      description:
        "Taking responsibility and being held accountable by the people we serve",
    },
    {
      icon: FaEye,
      title: "Transparency",
      description:
        "Providing clear account of our actions and decisions to all stakeholders",
    },
  ];

  return (
    <AccountabilityContainer ref={sectionRef}>
      <ContentWrapper>
        <ContentGrid>
          <TextSection>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={
                isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }
              }
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <SectionHeader>
                <SectionTitle>
                  How Are We Held
                  <AccentText> Accountable</AccentText>
                </SectionTitle>
              </SectionHeader>

              <MainDescription>
                Accountability is one of the core values that helps KRO to
                provide the best possible service to the people it assists. It
                represents a change in mind-set from an almost exclusive focus
                on accountability towards donors to a recognition that KRO must
                also be accountable to the people that it assists and involve
                them in decisions that affect their lives.
              </MainDescription>

              <ActionSection>
                <ReadMoreButton>
                  <span>Learn More About Our Approach</span>
                  <FaArrowRight />
                </ReadMoreButton>
              </ActionSection>
            </motion.div>
          </TextSection>

          <ImageSection>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
            >
              <ImageContainer>
                <MainImage
                  src="https://res.cloudinary.com/dnoojbooq/image/upload/v1750245764/Triggring_fwde0s.webp"
                  alt="Community members gathered in a circle for accountability meeting"
                />
                <ImageOverlay />
                <ImageCaption>
                  <CaptionText>
                    Community members participating in accountability and
                    decision-making processes
                  </CaptionText>
                </ImageCaption>
              </ImageContainer>

              {/* <PrinciplesGrid>
                {accountabilityPrinciples.map((principle, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    animate={
                      isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }
                    }
                    transition={{ delay: 0.5 + index * 0.1, duration: 0.6 }}
                  >
                    <PrincipleCard>
                      <PrincipleIcon>
                        <principle.icon />
                      </PrincipleIcon>
                      <PrincipleContent>
                        <PrincipleTitle>{principle.title}</PrincipleTitle>
                        <PrincipleDescription>
                          {principle.description}
                        </PrincipleDescription>
                      </PrincipleContent>
                    </PrincipleCard>
                  </motion.div>
                ))}
              </PrinciplesGrid> */}
            </motion.div>
          </ImageSection>
        </ContentGrid>
      </ContentWrapper>
    </AccountabilityContainer>
  );
};

export default AccountabilitySection;

// Styled Components
const AccountabilityContainer = styled.section`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  padding: 6rem 0;
  position: relative;

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const ContentWrapper = styled.div`
  /* max-width: 1200px; */
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  margin-bottom: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 3rem;
    text-align: center;
  }
`;

const TextSection = styled.div`
  @media (max-width: 1024px) {
    order: 2;
  }
`;

const ImageSection = styled.div`
  @media (max-width: 1024px) {
    order: 1;
  }
`;

const SectionHeader = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2.5rem, 4vw, 3.5rem);
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
  margin: 0;
  letter-spacing: -0.02em;
`;

const AccentText = styled.span`
  color: #10b981;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: -4px;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #10b981, #059669);
    border-radius: 2px;
  }
`;

const MainDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  color: #64748b;
  margin: 0 0 2rem 0;
`;

const PrinciplesGrid = styled.div`
  display: grid;
  gap: 0.75rem;
  margin: 1.5rem 0 0 0;
`;

const PrincipleCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 0.75rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  border: 1px solid #f1f5f9;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const PrincipleIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #10b981, #059669);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.1rem;
  flex-shrink: 0;
`;

const PrincipleContent = styled.div`
  flex: 1;
`;

const PrincipleTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
`;

const PrincipleDescription = styled.p`
  font-size: 0.9rem;
  color: #64748b;
  line-height: 1.5;
  margin: 0;
`;

const ActionSection = styled.div`
  margin-top: 2rem;
`;

const ReadMoreButton = styled.button`
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);

    svg {
      transform: translateX(3px);
    }
  }

  svg {
    transition: transform 0.3s ease;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.2);
  }
`;

const MainImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  transition: transform 0.3s ease;

  ${ImageContainer}:hover & {
    transform: scale(1.02);
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40%;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.6), transparent);
  pointer-events: none;
`;

const ImageCaption = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1.5rem;
  z-index: 2;
`;

const CaptionText = styled.p`
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;
